import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import "./SideSheet.css";
import {
  XMarkIcon,
  UserCircleIcon,
  BellIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "@tanstack/react-router";

export default function SideSheet({ open, setOpen, user, notificationStatus, onLogout }) {
  const navigate = useNavigate();
  const handleNavigation = (path) => {
    setOpen(false);
    navigate({ to: path, resetScroll: false });
  };

  const notificationStatusItem = {
    id: "notifications",
    name: "Ειδοποιήσεις",
    icon: BellIcon,
    description: "Κατάσταση ειδοποιήσεων",
    status: notificationStatus,
    isStatusOnly: true,
  };

  const menuItems = [
    {
      id: "my-program",
      name: "Το Πρόγραμμά μου",
      icon: CalendarIcon,
      description: "Οι δικές σου ομιλίες",
      action: () => handleNavigation("/my-program"),
    },
    ...(user?.role === "admin" ? [{
      id: "user-settings",
      name: "Διαχείριση Χρηστών",
      icon: CogIcon,
      description: "Επεξεργασία και διαγραφή χρηστών",
      action: () => handleNavigation("/user-settings"),
    }] : []),
  ];

  const getNotificationStatusIcon = (status) => {
    switch (status) {
      case "On":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "Off":
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <XCircleIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getNotificationStatusText = (status) => {
    switch (status) {
      case "On":
        return "Ενεργές";
      case "Off":
        return "Ανενεργές";
      default:
        return "Μη διαθέσιμες";
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={(value) => {
          // Only close if explicitly set to false, prevents rapid toggling
          if (value === false) {
            setOpen(false);
          }
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={() => setOpen(false)}
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen max-w-md">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4"
                  >
                    <div className="">
                      <button
                        type="button"
                        className="relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setOpen(false);
                        }}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  
                  <div 
                    className="flex h-full flex-col bg-white shadow-xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="bg-gray-800 px-6 py-8 relative overflow-hidden flex-shrink-0">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900"></div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                      
                      <div className="relative flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/30 transition-all duration-300 hover:ring-white/50 hover:scale-105">
                            <UserCircleIcon className="h-10 w-10 text-white" />
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <h2 className="text-xl font-semibold text-white mb-1">
                            Καλώς ήρθες!
                          </h2>
                          <div className="flex items-center mt-1 opacity-90 hover:opacity-100 transition-opacity">
                            <EnvelopeIcon className="h-4 w-4 text-gray-300 mr-2" />
                            <p className="text-sm text-gray-300 truncate">
                              {user?.email || "Φόρτωση..."}
                            </p>
                          </div>
                          <div className="flex items-center mt-1 opacity-90 hover:opacity-100 transition-opacity">
                            <ShieldCheckIcon className="h-4 w-4 text-gray-300 mr-2" />
                            <p className="text-sm text-gray-300 capitalize">
                              {user?.role === "admin" ? "Διαχειριστής" : "Χρήστης"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content Area - Scrollable */}
                    <div className="flex-1 overflow-y-auto">
                      {/* Notification Status - Separate Section */}
                      <div className="px-6 pt-6 pb-4">
                        <div
                          className={`w-full relative rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border border-blue-200 shadow-sm ${open ? 'side-sheet-menu-item' : ''}`}
                          style={{
                            animationDelay: '0ms'
                          }}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                                <notificationStatusItem.icon className="h-6 w-6 text-blue-700" aria-hidden="true" />
                              </div>
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900">{notificationStatusItem.name}</p>
                                <div className="flex items-center space-x-2">
                                  {getNotificationStatusIcon(notificationStatusItem.status)}
                                  <span className="text-xs font-medium text-gray-700">
                                    {getNotificationStatusText(notificationStatusItem.status)}
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">{notificationStatusItem.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="px-6 pb-6">
                        <div className="mb-3">
                          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Μενου</h3>
                        </div>
                        <div className="space-y-4">
                          {menuItems.map((item, index) => (
                            <button
                              key={item.id}
                              onClick={item.action}
                              className={`w-full group relative rounded-xl bg-gray-50 p-4 text-left hover:bg-gray-100 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] ${open ? 'side-sheet-menu-item' : ''}`}
                              style={{
                                animationDelay: `${(index + 1) * 100}ms`
                              }}
                            >
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-200 group-hover:bg-gray-300 transition-colors">
                                    <item.icon className="h-6 w-6 text-gray-700" aria-hidden="true" />
                                  </div>
                                </div>
                                <div className="ml-4 flex-1">
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Footer - Logout */}
                    <div className="border-t border-gray-200 px-6 py-4 flex-shrink-0">
                      <button
                        onClick={onLogout}
                        className="w-full flex items-center justify-center px-4 py-3 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200 group"
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2 group-hover:translate-x-1 transition-transform" />
                        Αποσύνδεση
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
